import React from 'react';
import { AccountantInfoTooltip } from '../../Accountant/AccountantInfoTooltip';

export type BlogSeoFormValues = {
  metaTitle: string;
  metaDescription: string;
  slug: string;
  focusKeyword: string;
  secondaryKeywords: string;
  recommendedSchema: string;
};

/** Accepts blog payloads where SEO columns may be null from the API. */
export type BlogSeoSource = {
  [K in keyof BlogSeoFormValues]?: string | null;
};

export const emptyBlogSeoValues = (): BlogSeoFormValues => ({
  metaTitle: '',
  metaDescription: '',
  slug: '',
  focusKeyword: '',
  secondaryKeywords: '',
  recommendedSchema: '',
});

export const blogSeoFromBlog = (blog?: BlogSeoSource | null): BlogSeoFormValues => ({
  metaTitle: blog?.metaTitle || '',
  metaDescription: blog?.metaDescription || '',
  slug: blog?.slug || '',
  focusKeyword: blog?.focusKeyword || '',
  secondaryKeywords: blog?.secondaryKeywords || '',
  recommendedSchema: blog?.recommendedSchema || '',
});

type FieldProps = {
  label: string;
  hint: React.ReactNode;
  children: React.ReactNode;
};

const SeoField: React.FC<FieldProps> = ({ label, hint, children }) => (
  <div>
    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
      <span>{label}</span>
      <AccountantInfoTooltip content={hint} label={`${label} help`} panelClassName="w-80" />
    </label>
    {children}
  </div>
);

const inputClass =
  'w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

type BlogSeoFieldsProps = {
  value: BlogSeoFormValues;
  onChange: (next: BlogSeoFormValues) => void;
};

/**
 * SEO Meta Package fields for writer create/edit blog forms.
 * Matches the SEO manager recommended structure.
 */
const BlogSeoFields: React.FC<BlogSeoFieldsProps> = ({ value, onChange }) => {
  const set =
    (key: keyof BlogSeoFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...value, [key]: e.target.value });
    };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/40 p-4 space-y-4">
      <div>
        <div className="text-base font-semibold text-gray-900 dark:text-white">SEO Meta Package</div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Optional but recommended. Used for search results, blog URLs, and structured data.
          Hover the <span className="font-medium">i</span> icon for guidance on each field.
        </p>
      </div>

      <SeoField
        label="Meta Title"
        hint={
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">What is Meta Title?</p>
            <p className="mt-2">
              The title shown in Google search results and browser tabs. Keep it clear and keyword-rich.
            </p>
            <p className="mt-2">
              <span className="font-medium">Tip:</span> aim for ~50–60 characters. Include the main topic + intent words
              like Causes, Symptoms, Treatment, Prevention.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Example: Malaria: Causes, Symptoms, Treatment &amp; Prevention
            </p>
          </>
        }
      >
        <input
          value={value.metaTitle}
          onChange={set('metaTitle')}
          className={inputClass}
          placeholder="Malaria: Causes, Symptoms, Treatment & Prevention"
          maxLength={255}
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {value.metaTitle.length}/60 recommended (max 255)
        </div>
      </SeoField>

      <SeoField
        label="Meta Description"
        hint={
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">What is Meta Description?</p>
            <p className="mt-2">
              Short summary shown under the title in search results. It should explain the page and encourage clicks.
            </p>
            <p className="mt-2">
              <span className="font-medium">Tip:</span> aim for ~140–160 characters. Mention the topic, who it helps,
              and what the reader will learn.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Example: Malaria is a mosquito-borne parasitic disease common in tropical regions. Learn its causes,
              symptoms, treatment options and how to prevent infection.
            </p>
          </>
        }
      >
        <textarea
          value={value.metaDescription}
          onChange={set('metaDescription')}
          rows={3}
          className={inputClass}
          placeholder="Malaria is a mosquito-borne parasitic disease common in tropical regions..."
          maxLength={500}
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {value.metaDescription.length}/160 recommended (max 500)
        </div>
      </SeoField>

      <SeoField
        label="URL Slug"
        hint={
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">What is URL Slug?</p>
            <p className="mt-2">
              The last part of the blog URL. Use lowercase words separated by hyphens. No spaces or special characters.
            </p>
            <p className="mt-2">
              You can enter just the slug, or a full path like <code>/blog/...</code> — we will normalize it.
            </p>
            <p className="mt-2">
              Leave blank to auto-generate from the blog title.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Example: malaria-causes-symptoms-treatment-prevention
            </p>
          </>
        }
      >
        <div className="flex items-stretch rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
          <span className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border-r border-gray-300 dark:border-gray-700 whitespace-nowrap">
            /blog/
          </span>
          <input
            value={value.slug}
            onChange={set('slug')}
            className="flex-1 px-3 py-3 bg-transparent text-gray-900 dark:text-gray-100 outline-none"
            placeholder="malaria-causes-symptoms-treatment-prevention"
            maxLength={255}
          />
        </div>
      </SeoField>

      <SeoField
        label="Focus Keyword"
        hint={
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">What is Focus Keyword?</p>
            <p className="mt-2">
              The single primary keyword / search phrase this blog should rank for. Use it naturally in the title,
              meta description, headings, and body.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Example: malaria</p>
          </>
        }
      >
        <input
          value={value.focusKeyword}
          onChange={set('focusKeyword')}
          className={inputClass}
          placeholder="malaria"
          maxLength={120}
        />
      </SeoField>

      <SeoField
        label="Secondary Keywords"
        hint={
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">What are Secondary Keywords?</p>
            <p className="mt-2">
              Related phrases that support the focus keyword. Separate multiple keywords with commas.
            </p>
            <p className="mt-2">
              Include symptom, cause, treatment, prevention style variants and important medical terms where relevant.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Example: malaria symptoms, malaria causes, malaria treatment, malaria prevention, Anopheles mosquito,
              Plasmodium falciparum
            </p>
          </>
        }
      >
        <textarea
          value={value.secondaryKeywords}
          onChange={set('secondaryKeywords')}
          rows={3}
          className={inputClass}
          placeholder="malaria symptoms, malaria causes, malaria treatment, malaria prevention..."
        />
      </SeoField>

      <SeoField
        label="Recommended Schema"
        hint={
          <>
            <p className="font-medium text-gray-900 dark:text-gray-100">What is Recommended Schema?</p>
            <p className="mt-2">
              Structured-data types (Schema.org) that help search engines understand the page. List one or more types,
              separated by commas.
            </p>
            <p className="mt-2">
              Common for health blogs: <span className="font-medium">Article</span>,{' '}
              <span className="font-medium">MedicalWebPage</span>,{' '}
              <span className="font-medium">MedicalCondition</span>.
            </p>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Example: MedicalWebPage, MedicalCondition, Article
            </p>
          </>
        }
      >
        <input
          value={value.recommendedSchema}
          onChange={set('recommendedSchema')}
          className={inputClass}
          placeholder="MedicalWebPage, MedicalCondition, Article"
          maxLength={255}
        />
      </SeoField>
    </div>
  );
};

export default BlogSeoFields;
